import { HttpApiError, HttpApiGroup, HttpApiSchema } from "@effect/platform";
import { Schema } from "effect";
import { endpoint } from "../auth/endpoint";
import { TelnyxVerification } from "../auth/telnyx-middleware";
import { TooManyRequests, UnprocessableEntity } from "../error/http-api-errors";
import { AccountId } from "../ids/definitions";
import { WebhookSuccessResponse } from "../models/webhook";

/**
 * Webhook endpoint group
 *
 * This group handles incoming webhooks from various external services.
 * Webhooks are unauthenticated endpoints (no Authorization middleware)
 * and rely on service-specific verification mechanisms (e.g., signature validation).
 *
 * Design principles:
 * - Each webhook type gets its own endpoint
 * - All endpoints return WebhookSuccessResponse for successful processing
 * - Non-retriable errors return 200 OK to prevent retries
 * - Transient errors return appropriate status codes to trigger retries:
 *   - 429 TooManyRequests for rate limiting
 *   - 503 ServiceUnavailable for temporary service issues
 * - Service-specific verification happens in the handler layer
 */
export const WebhookGroup = HttpApiGroup.make("webhook")
  .add(
    endpoint.webhook
      .post("gmail", "/webhooks/gmail")
      .addSuccess(WebhookSuccessResponse)
      .addError(HttpApiError.BadRequest)
      .addError(HttpApiError.Unauthorized)
      .addError(TooManyRequests)
      .addError(UnprocessableEntity)
      .addError(HttpApiError.ServiceUnavailable),
  )
  .add(
    endpoint.webhook
      .post("outlook", "/webhooks/outlook")
      .setUrlParams(
        Schema.Struct({ validationToken: Schema.optional(Schema.String) }),
      )
      .addSuccess(WebhookSuccessResponse)
      .addSuccess(
        Schema.String.pipe(
          HttpApiSchema.withEncoding({
            kind: "Text",
            contentType: "text/plain",
          }),
        ),
      )
      .addError(HttpApiError.BadRequest)
      .addError(HttpApiError.Unauthorized)
      .addError(TooManyRequests)
      .addError(UnprocessableEntity)
      .addError(HttpApiError.ServiceUnavailable),
  )
  .add(
    endpoint.public
      .post("outlookGraph", "/webhooks/outlook/graph")
      .setUrlParams(
        Schema.Struct({ validationToken: Schema.optional(Schema.String) }),
      )
      .addSuccess(WebhookSuccessResponse)
      .addSuccess(
        Schema.String.pipe(
          HttpApiSchema.withEncoding({
            kind: "Text",
            contentType: "text/plain",
          }),
        ),
      )
      .addError(HttpApiError.BadRequest)
      .addError(HttpApiError.ServiceUnavailable),
  )
  .add(
    endpoint.webhook
      .post("meta", "/webhooks/meta")
      .setUrlParams(
        Schema.Struct({
          "hub.mode": Schema.optional(Schema.String),
          "hub.verify_token": Schema.optional(Schema.String),
          "hub.challenge": Schema.optional(Schema.String),
        }),
      )
      .addSuccess(WebhookSuccessResponse)
      .addSuccess(
        Schema.String.pipe(
          HttpApiSchema.withEncoding({
            kind: "Text",
            contentType: "text/plain",
          }),
        ),
      )
      .addError(HttpApiError.BadRequest)
      .addError(HttpApiError.Unauthorized)
      .addError(TooManyRequests)
      .addError(HttpApiError.ServiceUnavailable),
  )
  .add(
    endpoint.webhook
      .post("telnyxSms", "/webhooks/telnyx-sms")
      .middleware(TelnyxVerification)
      .addError(HttpApiError.BadRequest)
      .addError(HttpApiError.Unauthorized)
      .addError(HttpApiError.Conflict)
      .addError(TooManyRequests)
      .addError(HttpApiError.InternalServerError)
      .addError(HttpApiError.ServiceUnavailable),
  )
  .add(
    endpoint.webhook
      .post("telnyxVoice", "/webhooks/telnyx-voice")
      .middleware(TelnyxVerification)
      .addError(HttpApiError.BadRequest)
      .addError(HttpApiError.Unauthorized)
      .addError(TooManyRequests)
      .addError(HttpApiError.ServiceUnavailable),
  )
  .add(
    endpoint.webhook
      .post("attentive", "/webhooks/attentive")
      .addSuccess(WebhookSuccessResponse)
      .addError(HttpApiError.BadRequest)
      .addError(HttpApiError.Unauthorized)
      .addError(TooManyRequests)
      .addError(HttpApiError.ServiceUnavailable),
  )
  .add(
    endpoint.webhook
      .post("postscript", "/webhooks/postscript")
      .addSuccess(WebhookSuccessResponse)
      .addError(HttpApiError.BadRequest)
      .addError(HttpApiError.Unauthorized)
      .addError(TooManyRequests)
      .addError(HttpApiError.ServiceUnavailable),
  )
  .add(
    endpoint.webhook
      .post("tiktok", "/webhooks/tiktok")
      .addSuccess(WebhookSuccessResponse)
      .addError(HttpApiError.BadRequest)
      .addError(HttpApiError.Unauthorized)
      .addError(TooManyRequests)
      .addError(HttpApiError.ServiceUnavailable),
  )
  .add(
    // Postmark posts directly with Basic auth (no Hookdeck); PostmarkVerification
    // logs failed auth checks while in observation mode. The accountId is plumbed
    // via the relay URL path.
    endpoint.postmarkWebhook
      .post("postmarkInbound", "/webhooks/postmark-inbound/:accountId")
      .setPath(Schema.Struct({ accountId: AccountId }))
      .addSuccess(WebhookSuccessResponse)
      .addError(HttpApiError.BadRequest)
      .addError(HttpApiError.Unauthorized)
      .addError(HttpApiError.ServiceUnavailable),
  )
  .prefix("/api");
